import { inject, Injectable } from '@angular/core';
import { DialogHelper } from '@common/modules/dialog/dialog.helper';
import { RecordsSandbox } from '@common/modules/records/sandbox/records.sandbox.service';
import { IntellectualRecordsService } from '@common/services/intellectual-records/intellectual-records.service';
import { WorkflowService } from '@common/services/mediahaven/workflow.service';
import { SearchService, TaskAction, TaskId, UserStoreService, WaitForRecordStatusUpdate } from '@mediahaven/shared';
import {
    ApiFragment,
    BatchActions,
    BatchService,
    BulkSearchRequest,
    Combine,
    EvaluateMediaPayload,
    Family,
    FragmentId,
    makeSequentialRequests,
    PublicationPolicy,
    QueryPart,
    QueryPartStrategy,
    RecordId,
    RecordStatus,
    RecordTypesService,
    RecordTypeStructure,
    Restriction,
    Task,
    TaskKey,
    UserFunction,
    UserFunctions
} from '@stylehaven/components';
import { partition } from 'lodash-es';
import { Observable } from 'rxjs';
import { map, mapTo, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class PublishActionSandbox {
    private readonly searchService: SearchService = inject(SearchService);

    constructor(
        private readonly intellectualRecordsService: IntellectualRecordsService,
        private readonly userStoreService: UserStoreService,
        private readonly recordTypesService: RecordTypesService,
        private readonly recordsSandbox: RecordsSandbox,
        private readonly workflowService: WorkflowService,
        private readonly dialogHelper: DialogHelper,
        private readonly batchService: BatchService
    ) {}

    // #region Validation check
    public async validatePublishable(record: ApiFragment): Promise<boolean> {
        // Validate if Intellectual Record has any invalid children
        const isIntellectual = record.isIntellectualObject();
        const isData = record.getRecordStructure() === RecordTypeStructure.Data;

        // Validate invalid children if has Cascade publication policy
        const hasPublicationPolicy: boolean = this.recordTypesService.hasPublicationPolicy(
            record.getRecordType(),
            PublicationPolicy.Cascade
        );
        const invalidChildrenIds: RecordId[] =
            (isIntellectual || isData) && hasPublicationPolicy
                ? ((await this.intellectualRecordsService.getInvalidChildIds(record.getRecordId()).toPromise()) ?? [])
                : [];

        if (invalidChildrenIds.length > 0) {
            this.dialogHelper.invalidIntellectualRecordChildren([record], [invalidChildrenIds]);
            return false;
        }

        // Validate if Record that RequirePublishedParent have a published parent
        const isChild: boolean = record.isCandidateChild() || record.hasParent();
        const requirePublishedParent: boolean = this.recordTypesService.hasPublicationPolicy(
            record.getRecordType(),
            PublicationPolicy.RequirePublishedParent
        );

        if (!isChild || !requirePublishedParent) {
            return true;
        }

        const parent: ApiFragment | undefined = await this.recordsSandbox.getRecordDirectParent(record).toPromise();

        if (!parent) {
            console.error(`PublishActionSandbox:: Parent for record with id ${record.getMediaObjectId} not found`);
            return false;
        }

        if (requirePublishedParent && parent.get(Family.RightsManagement, 'RecordPhase') === 'Concept') {
            this.dialogHelper.unpublishedParents([record], [parent]);
            return false;
        }

        return true;
    }

    public async validateBulkPublishable(records: readonly ApiFragment[]): Promise<boolean> {
        // Validate if Intellectual Record has any invalid children
        const haveNoInvalidChildren: boolean = await this.validateInvalidChildren(records);

        if (!haveNoInvalidChildren) {
            return false;
        }

        // Validate if Reocords have no unpublished parents if required
        const haveNoUnpublishedParents: boolean = await this.validateUnpublishedParents(records);

        if (!haveNoUnpublishedParents) {
            return false;
        }

        return true;
    }

    private async validateInvalidChildren(records: readonly ApiFragment[]): Promise<boolean> {
        const intellectualRecords: ApiFragment[] = records.filter(fragment => fragment.isIntellectualObject());

        if (intellectualRecords.length === 0) {
            return true;
        }

        const invalidChildrenIds$: Observable<RecordId[]>[] = intellectualRecords.map(intellectualRecord =>
            this.intellectualRecordsService.getInvalidChildIds(intellectualRecord.getRecordId())
        );

        const invalidChildrenIds: RecordId[][] = (await makeSequentialRequests(invalidChildrenIds$).toPromise()) ?? [];
        const totalInvalidChildren: number = invalidChildrenIds.flatMap(ids => ids).length;

        if (totalInvalidChildren > 0) {
            this.dialogHelper.invalidIntellectualRecordChildren(intellectualRecords, invalidChildrenIds);
            return false;
        }

        return true;
    }

    private async validateUnpublishedParents(records: readonly ApiFragment[]): Promise<boolean> {
        // Validate if Record that RequirePublishedParent have a published parent
        const hasParent: ApiFragment[] = records.filter(record => record.isCandidateChild() || record.hasParent());
        const requirePublishedParent: ApiFragment[] = hasParent.filter(record =>
            this.recordTypesService.hasPublicationPolicy(
                record.getRecordType(),
                PublicationPolicy.RequirePublishedParent
            )
        );

        // Skip if no records require a published parent
        if (requirePublishedParent.length === 0) {
            return true;
        }

        const requiredPublishedParentIds: RecordId[] = requirePublishedParent
            .filter(record => record.hasParent())
            .map(record => record.getParentRecordId())
            .filter(id => !!id) as RecordId[];

        const requiredPublishedCandidateParentIds: FragmentId[] = requirePublishedParent
            .filter(record => record.isCandidateChild())
            .map(record => record.getCandidateParentId())
            .filter(id => !!id) as FragmentId[];

        const hasRecordIdFilter: QueryPart = new QueryPart({
            field: 'RecordId',
            values: requiredPublishedParentIds,
            strategy: QueryPartStrategy.MatchAtLeastOne
        });
        const hasFragmentIdFilter: QueryPart = new QueryPart({
            field: 'FragmentId',
            values: requiredPublishedCandidateParentIds,
            strategy: QueryPartStrategy.MatchAtLeastOne
        });
        const isNotPublishedFilter: QueryPart = new QueryPart({
            field: 'RecordPhase',
            values: ['Concept'],
            strategy: QueryPartStrategy.MatchAll
        });

        const idQueryParts: QueryPart[] = [hasRecordIdFilter, hasFragmentIdFilter].filter(
            queryPart => queryPart.values.length > 0
        );

        // Is not published and has one of the required recordIds or fragmentIds
        const restriction: Restriction = Combine.And(isNotPublishedFilter, Combine.Or(...idQueryParts));

        const requiredUnpublishedParents: ApiFragment[] = await this.recordsSandbox
            .getAllRecords([], restriction)
            .toPromise();

        if (requiredUnpublishedParents.length > 0) {
            this.dialogHelper.unpublishedParents(requirePublishedParent, requiredUnpublishedParents);
            return false;
        }

        return true;
    }
    // #endregion

    // #region Publish Records
    public publishRecord(record: ApiFragment): Observable<boolean> {
        return this.userStoreService
            .getUserFunctions()
            .pipe(switchMap(user => this.executePublishAction(record, user)));
    }

    private executePublishAction(record: ApiFragment, userFunctions: UserFunctions): Observable<boolean> {
        const activeTask: Task | undefined = this.getActiveTask(record, userFunctions);

        if (activeTask && record.getRecordStatus() === RecordStatus.Submitted) {
            return this.executeWorkFlowPublish(activeTask);
        }
        return this.recordsSandbox.publishRecord(record).pipe(
            switchMap(() =>
                WaitForRecordStatusUpdate(record.getFragmentId(), this.searchService, record.getRecordStatus())
            ),
            mapTo(true)
        );
    }

    private getActiveTask(record: ApiFragment, userFunctions: UserFunctions): Task | undefined {
        const allTasks: Task[] = record.getTasks();
        return allTasks.find(
            task =>
                task.TypeId === TaskKey.EvaluateMedia &&
                task.AssigneeFunction &&
                userFunctions[task.AssigneeFunction as UserFunction]
        );
    }

    private executeWorkFlowPublish(task: Task): Observable<boolean> {
        const publishTaskAction: TaskAction = {
            ActionType: 'COMPLETE',
            CompletionVariables: {
                EvaluatorApproval: EvaluateMediaPayload.Approved
            }
        };

        return this.workflowService.submitTaskAction(task.Id as TaskId, publishTaskAction).pipe(mapTo(true));
    }

    public bulkPublishRecords(records: readonly ApiFragment[]): Observable<boolean[]> {
        return this.userStoreService.getUserFunctions().pipe(
            map(functions =>
                records.map(
                    record => [record, this.getActiveTask(record, functions)] as [ApiFragment, Task | undefined]
                )
            ),
            switchMap(recordTuples => makeSequentialRequests(this.separateWorkFlowAndBatch(recordTuples)))
        );
    }

    private separateWorkFlowAndBatch(recordTuples: [ApiFragment, Task | undefined][]): Observable<boolean>[] {
        const [sequential, batch] = partition(
            recordTuples,
            ([record, task]) => task && record.getRecordStatus() === RecordStatus.Submitted
        );

        // If record has an active task, use sequential requests
        const requests$: Observable<boolean>[] = sequential.map(([record, task]) =>
            this.executeWorkFlowPublish(task as Task)
        );

        if (batch.length > 0) {
            // If record does not have an active task, add it to a batch
            const batchRecords: ApiFragment[] = batch.map(([record, task]) => record);
            const searchRequest: BulkSearchRequest = new BulkSearchRequest(
                batchRecords,
                this.recordsSandbox.getAllRecordsZone()
            );
            const batchRequest$: Observable<boolean> = this.batchService
                .sendBatch(BatchActions.PUBLISH, {
                    fragments: batchRecords,
                    searchRequest: searchRequest,
                    waitForFinish: true,
                    isBulk: true
                })
                .pipe(mapTo(true));
            requests$.push(batchRequest$);
        }

        return requests$;
    }
    // #endregion
}
